import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  DollarSign,
  Users,
  MessageSquare,
  Sparkles,
  Target,
  TrendingUp,
  ArrowRight,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  ChevronRight,
  BarChart2,
  Loader2,
  ChevronDown,
  TrendingDown,
  Activity,
  Star,
  LayoutDashboard,
  HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { useTheme } from '../hooks/useTheme';
import { useTimeRange } from '../contexts/TimeRangeContext';
import AppHeader from '../components/layout/AppHeader';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import PageContainer from '../components/layout/PageContainer';
import {
  getDashboardMetrics,
  getUserFunnelData,
  getAIFeaturesData,
  fetchFeedback,
  getTimeSeriesData,
  getMetricsForPeriod,
  getPeriodComparison,
  testFirebaseConnection,
  getPlanPopularityData
} from '../services/analyticsService';
import { PlanPopularityCard } from '../components/revenue';
import { getTimeBasedGoals, getGoalComparison, sanitizeMetric } from '../lib/goalCalculations';
import GoalsStatus from '../components/GoalsStatus';

const MetricCard = ({ title, subtitle, value, change, icon: Icon, onClick }) => {
  const isPositive = change >= 0;
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (title === 'Total Users') {
      navigate('/users/insights');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        className={`group relative overflow-hidden bg-card text-card-foreground hover:shadow-lg transition-all duration-200 h-full ${onClick || title === 'Total Users' ? 'cursor-pointer hover:scale-[1.02]' : ''
          }`}
        onClick={handleClick}
      >
        <CardHeader className="flex flex-col items-start space-y-0 pb-4 sm:pb-6">
          <div className="flex items-center justify-between w-full mb-3 sm:mb-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-2 sm:p-3 rounded-xl bg-primary/10 group-hover:bg-primary/15 transition-colors">
                <Icon className="h-4 w-4 sm:h-6 sm:w-6 text-primary/70 group-hover:text-primary/90 transition-colors" />
              </div>
            </div>
            <div className={`text-xs sm:text-sm font-medium px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg transition-colors ${isPositive
              ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30'
              : 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30'
              }`}>
              <span className="inline-flex items-center gap-1 sm:gap-1.5">
                {isPositive ? (
                  <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                ) : (
                  <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4" />
                )}
                <span className="hidden sm:inline">{Math.abs(change).toFixed(1)}%</span>
                <span className="sm:hidden">{Math.abs(change).toFixed(0)}%</span>
              </span>
            </div>
          </div>
          <div className="space-y-1">
            <CardTitle className="text-sm sm:text-lg font-semibold text-foreground/95">
              {title}
            </CardTitle>
            {subtitle && (
              <p className="text-xs sm:text-sm text-muted-foreground/70">
                {subtitle}
              </p>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="text-xl sm:text-3xl font-bold tracking-tight text-foreground/95">
            {value}
          </div>
          {title === 'Revenue' && (
            <div className="mt-3 pt-3 border-t border-border/50">
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = '/revenue/details';
                }}
              >
                <DollarSign className="h-3 w-3 mr-1" />
                View Transaction Details
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

const GoalTracker = ({ current, target, projection }) => {
  const progress = Math.min((current / target) * 100, 100);
  const projectedProgress = Math.min((projection / target) * 100, 100);

  return (
    <Card className="group relative overflow-hidden bg-card text-card-foreground hover:shadow-md transition-all duration-200">
      <CardHeader className="pb-3 sm:pb-4">
        <CardTitle className="text-base sm:text-lg flex items-center gap-2 sm:gap-3">
          <div className="p-2 sm:p-2.5 rounded-lg bg-primary/5 group-hover:bg-primary/10 dark:group-hover:bg-primary/20 transition-colors">
            <Target className="h-4 w-4 sm:h-5 sm:w-5 text-primary/50 group-hover:text-primary/80 dark:group-hover:text-primary/90 transition-colors" />
          </div>
          <span className="text-foreground/90 dark:text-foreground/80 font-medium">6-Month Goal Progress</span>
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm text-muted-foreground">
          Target: ${target.toLocaleString()}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="space-y-3 sm:space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="h-3 w-3 sm:h-4 sm:w-4 text-primary/50" />
                <span className="text-xs sm:text-sm text-muted-foreground">Current Progress</span>
              </div>
              <span className="text-xs sm:text-sm font-medium">{progress.toFixed(1)}%</span>
            </div>
            <div className="h-1 sm:h-1.5 rounded-full bg-primary/10 overflow-hidden">
              <motion.div
                className={`h-full ${progress >= 75 ? 'bg-[hsl(142.1_76.2%_36.3%)]' : 'bg-primary'}`}
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 text-primary/50" />
                <span className="text-xs sm:text-sm text-muted-foreground">AI Projection</span>
              </div>
              <span className="text-xs sm:text-sm font-medium">{projectedProgress.toFixed(1)}%</span>
            </div>
            <div className="h-1 sm:h-1.5 rounded-full bg-primary/10 overflow-hidden">
              <motion.div
                className="h-full bg-primary/50"
                initial={{ width: 0 }}
                animate={{ width: `${projectedProgress}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const UserFunnel = ({ data }) => {
  const stages = data?.stages || [];

  return (
    <Card className="relative overflow-hidden bg-card/50 hover:bg-card/80 transition-all duration-300 border border-border/50 rounded-2xl shadow-sm hover:shadow-md">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-primary/[0.01] to-transparent pointer-events-none" />
      <CardHeader className="pb-2">
        <div className="space-y-1">
          <CardTitle className="text-base sm:text-lg font-medium text-foreground">User Funnel</CardTitle>
          <CardDescription className="text-xs sm:text-sm text-muted-foreground">User journey through key features</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-4 sm:space-y-6">
          {stages.length > 0 && stages.some(stage => stage.count > 0) ? (
            stages.map((stage, index) => (
              <motion.div
                key={index}
                className="space-y-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "w-2 h-2 rounded-full",
                      index === 0 ? "bg-blue-500" :
                        index === 1 ? "bg-indigo-500" :
                          index === 2 ? "bg-orange-500" :
                            "bg-emerald-500"
                    )} />
                    <span className="text-xs sm:text-sm font-medium text-foreground/90">{stage.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm font-bold text-foreground">{stage.count.toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground/70">{stage.percentage}%</span>
                  </div>
                </div>
                <div className="h-1.5 sm:h-2 rounded-full bg-muted/30 overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-primary to-primary/80"
                    initial={{ width: 0 }}
                    animate={{ width: `${stage.percentage}%` }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  />
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-8 text-center"
            >
              <Users className="h-8 w-8 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground mb-2">No user funnel data yet</p>
              <p className="text-xs text-muted-foreground/70">User journey data will appear here</p>
            </motion.div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const AIFeatureUsage = ({ data }) => {
  return (
    <Card className="relative overflow-hidden bg-card/50 hover:bg-card/80 transition-all duration-300 border border-border/50 rounded-2xl shadow-sm hover:shadow-md">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-primary/[0.01] to-transparent pointer-events-none" />
      <CardHeader className="pb-2">
        <div className="space-y-1">
          <CardTitle className="text-base sm:text-lg font-medium text-foreground">AI Feature Usage</CardTitle>
          <CardDescription className="text-xs sm:text-sm text-muted-foreground">AI feature adoption across users</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3 sm:space-y-4">
          {data.length > 0 ? (
            data.map((feature, index) => (
              <motion.div
                key={feature.name}
                className="space-y-2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-muted-foreground">{feature.name}</span>
                  <span className="text-xs sm:text-sm font-medium">
                    {feature.name.includes('Events') ? feature.usage : `${feature.usage}%`}
                  </span>
                </div>
                <div className="h-1 sm:h-1.5 rounded-full bg-primary/10 overflow-hidden">
                  <motion.div
                    className="h-full bg-primary"
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(feature.usage, 100)}%` }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  />
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-8 text-center"
            >
              <Sparkles className="h-8 w-8 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground mb-2">No AI usage data yet</p>
              <p className="text-xs text-muted-foreground/70">AI feature usage will appear here</p>
            </motion.div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const RecentFeedback = ({ data }) => {
  return (
    <Card className="relative overflow-hidden bg-card/50 hover:bg-card/80 transition-all duration-300 border border-border/50 rounded-2xl shadow-sm hover:shadow-md">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-primary/[0.01] to-transparent pointer-events-none" />
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base sm:text-lg font-medium text-foreground">Recent Feedback</CardTitle>
            <CardDescription className="text-xs sm:text-sm text-muted-foreground">Latest user feedback and suggestions</CardDescription>
          </div>
          <Link to="/feedback" className="text-xs sm:text-sm text-primary hover:text-primary/80 transition-colors">
            View All
            <ChevronRight className="ml-1 h-3 w-3 sm:h-4 sm:w-4 inline" />
          </Link>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1.5">
          {data.length > 0 ? (
            data.slice(0, 3).map((feedback, index) => (
              <motion.div
                key={feedback.id}
                initial={{ opacity: 0, y: 2 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -2 }}
                transition={{ duration: 0.12, ease: [0.23, 1, 0.32, 1] }}
                className={cn(
                  "group cursor-pointer transition-all duration-150 p-3 sm:p-4 rounded-xl",
                  "hover:bg-muted/40 active:bg-muted/60",
                  "border border-border/30 hover:border-border/50",
                  index === 0 && "bg-muted/20"
                )}
              >
                <div className="flex items-start gap-2 sm:gap-3">
                  {/* Avatar */}
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-muted/60 flex items-center justify-center text-foreground/70 text-xs sm:text-sm font-medium shrink-0 ring-1 ring-border/30">
                    {feedback.user.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0 space-y-1.5 sm:space-y-2">
                    {/* Name and Rating */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                        <h4 className="font-medium text-xs sm:text-sm text-foreground truncate">
                          {feedback.user}
                        </h4>
                        <span className="text-xs text-muted-foreground font-mono">
                          #{feedback.id}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
                        <div className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={cn(
                                "h-2.5 w-2.5 sm:h-3 sm:w-3",
                                i < (feedback.rating || 3)
                                  ? 'text-foreground/80 fill-foreground/80'
                                  : 'text-muted-foreground/30'
                              )}
                            />
                          ))}
                        </div>
                        <span className="text-base sm:text-lg ml-0.5">
                          {feedback.sentiment === 'positive' ? '😊' : feedback.sentiment === 'negative' ? '😔' : '😐'}
                        </span>
                      </div>
                    </div>

                    {/* Feedback */}
                    <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                      {feedback.message}
                    </p>

                    {/* Metadata */}
                    <div className="flex items-center gap-1 sm:gap-2 text-xs text-muted-foreground">
                      <span>{feedback.date}</span>
                      <span>•</span>
                      <span className={cn(
                        "px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium",
                        feedback.sentiment === 'positive'
                          ? "bg-emerald-500/10 text-emerald-500"
                          : feedback.sentiment === 'negative'
                            ? "bg-red-500/10 text-red-500"
                            : "bg-yellow-500/10 text-yellow-500"
                      )}>
                        {feedback.sentiment}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-8 text-center"
            >
              <MessageSquare className="h-8 w-8 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground mb-2">No feedback yet</p>
              <p className="text-xs text-muted-foreground/70">User feedback will appear here</p>
            </motion.div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const QuickInsights = ({ data }) => {
  return (
    <Card className="relative overflow-hidden bg-card/50 hover:bg-card/80 transition-all duration-300 border border-border/50 rounded-2xl shadow-sm hover:shadow-md">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-primary/[0.01] to-transparent pointer-events-none" />
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base sm:text-lg font-medium text-foreground">Quick Insights</CardTitle>
            <CardDescription className="text-xs sm:text-sm text-muted-foreground">Key metrics and trends</CardDescription>
          </div>
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
            <span className="hidden sm:inline">View All</span>
            <ChevronRight className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {data.length > 0 ? (
            data.map((insight, index) => (
              <motion.div
                key={insight.metric}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={cn(
                  "group p-3 sm:p-4 rounded-xl border transition-all duration-300",
                  "hover:shadow-md hover:border-border/80",
                  "bg-card/50 hover:bg-card/80",
                  "relative overflow-hidden"
                )}
              >
                {/* Decorative gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-transparent to-primary/[0.02] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className="relative space-y-2 sm:space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <p className="text-xs sm:text-sm font-medium text-foreground">{insight.metric}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">{insight.description}</p>
                    </div>
                    <div className={cn(
                      "flex items-center gap-1 px-1.5 sm:px-2 py-1 rounded-full text-xs font-medium transition-colors duration-300",
                      insight.trend === 'up'
                        ? "bg-emerald-500/10 text-emerald-500 group-hover:bg-emerald-500/20"
                        : "bg-red-500/10 text-red-500 group-hover:bg-red-500/20"
                    )}>
                      {insight.trend === 'up' ? (
                        <TrendingUp className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                      ) : (
                        <TrendingDown className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                      )}
                      <span className="hidden sm:inline">{insight.change}</span>
                    </div>
                  </div>

                  {/* Animated progress bar */}
                  <div className="h-1 w-full bg-primary/5 rounded-full overflow-hidden">
                    <motion.div
                      className={cn(
                        "h-full rounded-full",
                        insight.trend === 'up' ? "bg-emerald-500/50" : "bg-red-500/50"
                      )}
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 1, delay: index * 0.2 }}
                    />
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="col-span-full flex flex-col items-center justify-center py-8 text-center"
            >
              <BarChart2 className="h-8 w-8 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground mb-2">No insights data yet</p>
              <p className="text-xs text-muted-foreground/70">Key insights will appear here</p>
            </motion.div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Loading skeleton for metric cards
const MetricCardSkeleton = () => (
  <Card className="animate-pulse">
    <CardHeader className="pb-4">
      <div className="flex items-center justify-between w-full mb-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-muted" />
        </div>
        <div className="h-6 w-16 bg-muted rounded" />
      </div>
      <div className="space-y-2">
        <div className="h-4 w-24 bg-muted rounded" />
        <div className="h-3 w-32 bg-muted rounded" />
      </div>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="h-8 w-20 bg-muted rounded" />
    </CardContent>
  </Card>
);

const Overview = ({ onSettingsClick }) => {
  const { theme, isDark, toggleTheme } = useTheme();
  const { timeRange, getDateRange, isReloading } = useTimeRange();
  const [data, setData] = useState({
    metrics: [],
    funnel: { stages: [] },
    aiUsage: [],
    feedback: [],
    insights: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [lastReload, setLastReload] = useState(Date.now());
  const [goals, setGoals] = useState(null);
  const [planPopularityData, setPlanPopularityData] = useState({
    standardCount: 0,
    proCount: 0,
    totalPaywallViews: 0,
    standardConversion: 0,
    proConversion: 0,
    totalPurchases: 0
  });

  const fetchData = async () => {
    setIsLoading(true);
    try {
      console.log('🚀 Starting data fetch...');
      const { start: startDate, end: endDate } = getDateRange();
      const timeRange = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

      // Determine period based on time range
      let period = '30d';
      if (timeRange <= 7) period = '7d';
      else if (timeRange <= 30) period = '30d';
      else if (timeRange <= 90) period = '90d';
      else period = '180d';

      console.log('🔍 Fetching data for period:', period, 'timeRange:', timeRange);

      // Get goals for this time range
      const timeBasedGoals = getTimeBasedGoals(timeRange);
      setGoals(timeBasedGoals);

      // Fetch real data from Firebase with period-based comparison
      const [dashboardMetrics, funnelData, aiFeaturesData, feedbackData, periodComparison, planPopularity] = await Promise.all([
        getMetricsForPeriod(period).catch(err => {
          console.error('Error fetching dashboard metrics:', err);
          return {
            totalUsers: 0,
            activeUsers: 0,
            conversionRate: 0,
            totalRevenue: 0,
            resumeDownloads: 0,
            aiFeaturesUsed: 0,
            feedbackCount: 0,
            newUsers: 0,
            feedbackSatisfaction: 0,
            changes: { revenue: 0, newUsers: 0, feedback: 0, aiUsage: 0 },
            goals: timeBasedGoals
          };
        }),
        getUserFunnelData(timeRange).catch(err => {
          console.error('Error fetching funnel data:', err);
          return { new: 0, engaged: 0, started: 0, saved: 0, paid: 0 };
        }),
        getAIFeaturesData(timeRange).catch(err => {
          console.error('Error fetching AI features data:', err);
          return {
            totalUsers: 0,
            suggestionViewed: 0,
            suggestionApplied: 0,
            autofill: 0,
            optimizeTriggered: 0,
            optimizeApplied: 0,
            suggestionAcceptanceRate: 0,
            optimizeAcceptanceRate: 0
          };
        }),
        fetchFeedback(10).catch(err => {
          console.error('Error fetching feedback:', err);
          return [];
        }),
        getPeriodComparison(period, period).catch(err => {
          console.error('Error fetching period comparison:', err);
          return {
            changes: { revenue: 0, newUsers: 0, feedback: 0, aiUsage: 0 },
            goals: timeBasedGoals
          };
        }),
        getPlanPopularityData(timeRange).catch(err => {
          console.error('Error fetching plan popularity data:', err);
          return {
            standardCount: 0,
            proCount: 0,
            totalPaywallViews: 0,
            standardConversion: 0,
            proConversion: 0,
            totalPurchases: 0
          };
        })
      ]);

      console.log('📊 Dashboard metrics:', dashboardMetrics);
      console.log('🔄 Period comparison:', periodComparison);
      console.log('🤖 AI Usage value:', dashboardMetrics.aiFeaturesUsed);
      console.log('🤖 AI Features data:', aiFeaturesData);
      console.log('💰 Plan popularity data:', planPopularity);
      console.log('🎯 Goals set:', timeBasedGoals);

      setPlanPopularityData(planPopularity);

      // Calculate goal-based comparisons
      const revenueComparison = getGoalComparison(
        sanitizeMetric(dashboardMetrics.totalRevenue || 0),
        periodComparison.changes?.revenue?.previous || 0,
        timeBasedGoals.revenue.total,
        'revenue'
      );

      const usersComparison = getGoalComparison(
        sanitizeMetric(dashboardMetrics.totalUsers || 0),
        periodComparison.changes?.newUsers?.previous || 0,
        timeBasedGoals.users.total,
        'users'
      );

      const feedbackComparison = getGoalComparison(
        sanitizeMetric(dashboardMetrics.feedbackSatisfaction || 0),
        periodComparison.changes?.feedback?.previous || 0,
        timeBasedGoals.feedback.satisfaction,
        'feedback'
      );

      const aiUsageComparison = getGoalComparison(
        sanitizeMetric(dashboardMetrics.aiFeaturesUsed || 0),
        periodComparison.changes?.aiUsage?.previous || 0,
        timeBasedGoals.ai.adoption.autofill + timeBasedGoals.ai.adoption.optimize,
        'aiUsage'
      );

      const realData = {
        metrics: [
          {
            title: 'Revenue',
            subtitle: 'period total',
            value: `$${sanitizeMetric(dashboardMetrics.totalRevenue || 0).toLocaleString()}`,
            change: sanitizeMetric(revenueComparison.change || 0),
            icon: DollarSign,
            timestamp: new Date(),
            onClick: () => {
              // Navigate to revenue details page when clicked
              window.location.href = '/revenue/details';
            }
          },
          {
            title: 'Total Users',
            subtitle: 'all-time',
            value: sanitizeMetric(dashboardMetrics.totalUsers || 0).toLocaleString(),
            change: sanitizeMetric(usersComparison.change || 0),
            icon: Users,
            timestamp: new Date(),
            onClick: () => {
              // Navigate to users page when clicked
              window.location.href = '/users';
            }
          },
          {
            title: 'Feedback',
            subtitle: 'satisfaction',
            value: `${sanitizeMetric(dashboardMetrics.feedbackSatisfaction || 0).toFixed(1)}%`,
            change: sanitizeMetric(feedbackComparison.change || 0),
            icon: MessageSquare,
            timestamp: new Date(),
            onClick: () => {
              // Navigate to feedback page when clicked
              window.location.href = '/feedback';
            }
          },
          {
            title: 'AI Usage',
            subtitle: 'active users',
            value: sanitizeMetric(aiFeaturesData.aiAutofillTriggered + aiFeaturesData.aiResumeOptimizeApplied || 0).toLocaleString(),
            change: sanitizeMetric(aiUsageComparison.change || 0),
            icon: Sparkles,
            timestamp: new Date()
          },
          {
            title: 'New Users',
            subtitle: 'period growth',
            value: sanitizeMetric(dashboardMetrics.newUsers || 0).toLocaleString(),
            change: sanitizeMetric(usersComparison.change || 0),
            icon: Users,
            timestamp: new Date()
          }
        ],
        funnel: {
          stages: [
            { name: 'New Users', count: sanitizeMetric(funnelData.new || 0), percentage: 100, timestamp: new Date() },
            { name: 'Engaged', count: sanitizeMetric(funnelData.engaged || 0), percentage: sanitizeMetric(funnelData.engaged || 0) > 0 ? sanitizeMetric(Math.round((sanitizeMetric(funnelData.engaged || 0) / Math.max(sanitizeMetric(funnelData.new || 0), 1)) * 100)) : 0, timestamp: new Date() },
            { name: 'Started Resume', count: sanitizeMetric(funnelData.started || 0), percentage: sanitizeMetric(funnelData.started || 0) > 0 ? sanitizeMetric(Math.round((sanitizeMetric(funnelData.started || 0) / Math.max(sanitizeMetric(funnelData.engaged || 0), 1)) * 100)) : 0, timestamp: new Date() },
            { name: 'Saved Resume', count: sanitizeMetric(funnelData.saved || 0), percentage: sanitizeMetric(funnelData.saved || 0) > 0 ? sanitizeMetric(Math.round((sanitizeMetric(funnelData.saved || 0) / Math.max(sanitizeMetric(funnelData.started || 0), 1)) * 100)) : 0, timestamp: new Date() },
            { name: 'Paid & Downloaded', count: sanitizeMetric(funnelData.paid || 0), percentage: sanitizeMetric(funnelData.paid || 0) > 0 ? sanitizeMetric(Math.round((sanitizeMetric(funnelData.paid || 0) / Math.max(sanitizeMetric(funnelData.saved || 0), 1)) * 100)) : 0, timestamp: new Date() }
          ]
        },
        aiUsage: [
          { name: 'AI Autofill Users', usage: aiFeaturesData.totalUsers > 0 ? Math.round((aiFeaturesData.aiAutofillTriggered / aiFeaturesData.totalUsers) * 100) : 0, timestamp: new Date() },
          { name: 'AI Optimize Users', usage: aiFeaturesData.totalUsers > 0 ? Math.round((aiFeaturesData.aiResumeOptimizeApplied / aiFeaturesData.totalUsers) * 100) : 0, timestamp: new Date() },
          { name: 'AI Tool Usage Rate', usage: aiFeaturesData.aiToolUsageRate || 0, timestamp: new Date() },
          { name: 'Total Autofill Events', usage: aiFeaturesData.totalAutofillEvents || 0, timestamp: new Date() },
          { name: 'Total Optimize Events', usage: aiFeaturesData.totalOptimizeEvents || 0, timestamp: new Date() }
        ].map(feature => ({
          ...feature,
          usage: feature.usage > 0 ? feature.usage : 0 // Ensure no negative values
        })),
        feedback: feedbackData.length > 0 ? feedbackData.map((item, index) => {
          // Transform real feedback data from Firebase
          const comment = item.comment || item.message || 'No message provided';
          const timestamp = item.timestamp || item.createdAt;
          const userId = item.userId || 'Anonymous User';
          const emoji = item.emoji || '📝';

          // Get user display info from fetched user data
          const userData = item.userData;
          const userDisplayName = userData?.displayName || userData?.email?.split('@')[0] || userId;

          // Convert emoji to rating for display purposes
          let rating = 3; // Default rating
          if (emoji) {
            if (emoji.includes('😃') || emoji.includes('😊') || emoji.includes('😄') || emoji.includes('😍') || emoji.includes('👍') || emoji.includes('❤️')) {
              rating = 5;
            } else if (emoji.includes('😐') || emoji.includes('🤔') || emoji.includes('😶')) {
              rating = 3;
            } else if (emoji.includes('😞') || emoji.includes('😡') || emoji.includes('👎') || emoji.includes('😭')) {
              rating = 1;
            }
          }

          // Determine sentiment based on emoji and comment content
          let sentiment = 'neutral';
          if (rating >= 4) sentiment = 'positive';
          else if (rating <= 2) sentiment = 'negative';

          return {
            id: item.id || index + 1,
            message: comment,
            user: userDisplayName,
            sentiment: sentiment,
            rating: rating,
            date: timestamp ? new Date(timestamp.toDate?.() || timestamp).toLocaleDateString() : 'Unknown',
            timestamp: timestamp ? new Date(timestamp.toDate?.() || timestamp) : new Date()
          };
        }) : [
          {
            id: 1,
            message: 'No feedback data available yet',
            user: 'System',
            sentiment: 'neutral',
            date: new Date().toLocaleDateString(),
            timestamp: new Date()
          }
        ],
        insights: [
          {
            metric: 'Revenue Growth',
            trend: revenueComparison.isPositive ? 'up' : 'down',
            change: `${revenueComparison.isPositive ? '+' : ''}${revenueComparison.change.toFixed(1)}%`,
            description: revenueComparison.isPositive ? 'Revenue is growing steadily' : 'Revenue needs attention',
            timestamp: new Date()
          },
          {
            metric: 'User Growth',
            trend: usersComparison.isPositive ? 'up' : 'down',
            change: `${usersComparison.isPositive ? '+' : ''}${usersComparison.change.toFixed(1)}%`,
            description: usersComparison.isPositive ? 'Strong user acquisition' : 'User acquisition declining',
            timestamp: new Date()
          },
          {
            metric: 'AI Adoption',
            trend: aiUsageComparison.isPositive ? 'up' : 'down',
            change: `${aiUsageComparison.isPositive ? '+' : ''}${aiUsageComparison.change.toFixed(1)}%`,
            description: aiUsageComparison.isPositive ? 'AI features gaining traction' : 'AI feature usage declining',
            timestamp: new Date()
          }
        ]
      };

      console.log('✅ Setting real data:', realData);
      setData(realData);
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      // Fallback to sample data structure
      const fallbackData = {
        metrics: [
          {
            title: 'Revenue',
            subtitle: 'period total',
            value: '$0',
            change: 0,
            icon: DollarSign,
            timestamp: new Date()
          },
          {
            title: 'Total Users',
            subtitle: 'all-time',
            value: '0',
            change: 0,
            icon: Users,
            timestamp: new Date()
          },
          {
            title: 'Feedback',
            subtitle: 'satisfaction',
            value: '0%',
            change: 0,
            icon: MessageSquare,
            timestamp: new Date()
          },
          {
            title: 'AI Usage',
            subtitle: 'active users',
            value: '0',
            change: 0,
            icon: Sparkles,
            timestamp: new Date()
          },
          {
            title: 'New Users',
            subtitle: 'period growth',
            value: '0',
            change: 0,
            icon: Users,
            timestamp: new Date()
          }
        ],
        funnel: {
          stages: [
            { name: 'New Users', count: 0, percentage: 100, timestamp: new Date() },
            { name: 'Engaged', count: 0, percentage: 0, timestamp: new Date() },
            { name: 'Started Resume', count: 0, percentage: 0, timestamp: new Date() },
            { name: 'Saved Resume', count: 0, percentage: 0, timestamp: new Date() },
            { name: 'Paid & Downloaded', count: 0, percentage: 0, timestamp: new Date() }
          ]
        },
        aiUsage: [
          { name: 'AI Suggestions Viewed', usage: 0, timestamp: new Date() },
          { name: 'AI Suggestions Applied', usage: 0, timestamp: new Date() },
          { name: 'AI Autofill', usage: 0, timestamp: new Date() },
          { name: 'AI Optimize Triggered', usage: 0, timestamp: new Date() },
          { name: 'AI Optimize Applied', usage: 0, timestamp: new Date() }
        ].map(feature => ({
          ...feature,
          usage: feature.usage > 0 ? feature.usage : 0
        })),
        feedback: [
          {
            id: 1,
            message: 'No feedback data available yet',
            user: 'System',
            sentiment: 'neutral',
            date: new Date().toLocaleDateString(),
            timestamp: new Date()
          }
        ],
        insights: [
          {
            metric: 'Revenue Growth',
            trend: 'up',
            change: '0%',
            description: 'No data available yet',
            timestamp: new Date()
          },
          {
            metric: 'User Growth',
            trend: 'up',
            change: '0%',
            description: 'No data available yet',
            timestamp: new Date()
          },
          {
            metric: 'AI Adoption',
            trend: 'up',
            change: '0%',
            description: 'No data available yet',
            timestamp: new Date()
          }
        ]
      };
      setData(fallbackData);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when time range changes or reload is triggered
  useEffect(() => {
    const initializeData = async () => {
      // First test Firebase connection
      const isConnected = await testFirebaseConnection();
      if (!isConnected) {
        console.error('❌ Firebase connection failed, using fallback data');
      }

      fetchData();
    };

    initializeData();
  }, [timeRange, lastReload]);

  // Show loading state when context is reloading
  useEffect(() => {
    if (isReloading) {
      setIsLoading(true);
    }
  }, [isReloading]);

  // Filter data based on time range
  const filteredData = useMemo(() => {
    // For real Firebase data, the filtering is already done in the fetchData function
    // based on the timeRange parameter, so we just return the data as is
    return data;
  }, [data, timeRange]);

  return (
    <div className="h-full flex flex-col">
      <AppHeader
        title="Overview"
        icon={LayoutDashboard}
        onSettingsClick={onSettingsClick}
        onThemeToggle={toggleTheme}
        isDarkMode={isDark}
      />
      <PageContainer>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
          {isLoading ? (
            <>
              <MetricCardSkeleton />
              <MetricCardSkeleton />
              <MetricCardSkeleton />
              <MetricCardSkeleton />
              <MetricCardSkeleton />
            </>
          ) : (
            filteredData.metrics.map((metric) => (
              <MetricCard key={metric.title} {...metric} />
            ))
          )}
        </div>

        {/* Plan Popularity Card - Show when there's revenue data */}
        {!isLoading && planPopularityData.totalPurchases > 0 && (
          <div className="mt-6">
            <PlanPopularityCard
              data={planPopularityData}
              loading={false}
            />
          </div>
        )}

        {/* Goals Status Card */}
        {!isLoading && goals && (
          <div className="mt-6">
            <GoalsStatus
              goals={goals}
              currentMetrics={data.metrics.reduce((acc, metric) => {
                if (metric.title === 'Revenue') acc.totalRevenue = parseFloat(metric.value.replace(/[$,]/g, ''));
                if (metric.title === 'Total Users') acc.totalUsers = parseInt(metric.value.replace(/,/g, ''));
                if (metric.title === 'AI Usage') acc.aiUsage = parseInt(metric.value.replace(/,/g, ''));
                return acc;
              }, {})}
              timeRange={Math.ceil((getDateRange().end - getDateRange().start) / (1000 * 60 * 60 * 24))}
            />
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
          {/* Left Column */}
          <div className="space-y-6 sm:space-y-8">
            {isLoading ? (
              <div className="space-y-6 sm:space-y-8">
                <Card className="h-[250px] sm:h-[300px] flex items-center justify-center">
                  <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-muted-foreground" />
                </Card>
                <Card className="h-[250px] sm:h-[300px] flex items-center justify-center">
                  <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-muted-foreground" />
                </Card>
              </div>
            ) : (
              <>
                <GoalTracker
                  current={75000}
                  target={100000}
                  projection={85000}
                />
                <UserFunnel data={filteredData.funnel} />
              </>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6 sm:space-y-8">
            {isLoading ? (
              <div className="space-y-6 sm:space-y-8">
                <Card className="h-[250px] sm:h-[300px] flex items-center justify-center">
                  <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-muted-foreground" />
                </Card>
                <Card className="h-[250px] sm:h-[300px] flex items-center justify-center">
                  <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-muted-foreground" />
                </Card>
              </div>
            ) : (
              <>
                <RecentFeedback data={filteredData.feedback} />
                <QuickInsights data={filteredData.insights} />
              </>
            )}
          </div>
        </div>
      </PageContainer>
    </div>
  );
};

export default Overview; 